import React, { useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import photoPlaceholder from 'assets/images/user-photo-placeholder.svg';
import styles from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { postWorklogCL } from 'store/currentWorklog/operations';
import TimeInfo from './components/TimeInfo/TimeInfo';
import { isMobileSchedule } from 'helpers/_helpers';
import { useLocation } from 'react-router-dom';
import currentWorklogActions from 'store/currentWorklog/actions';
import useWorklogStyles from 'components/WorklogDetails/hooks/useWorklogStyles';
import { useCanEditWorklogCrewLeader } from 'common/hooks/use-can-edit-worklog';
import { InfoTooltip } from 'lib';
import { selectLaborClassificationsState } from 'store/laborClassifications';
import { getWorkerLcOverrideNames } from 'helpers/laborClassification';

const CrewUser = ({
  classes,
  workerData,
  getFormattedHoursRange,
  hasAccess,
  openUserWorklogs,
  historyState,
  historyItem,
  removed,
  reserveLcIconSpace,
}) => {
  const worklogClasses = useWorklogStyles();

  const dispatch = useDispatch();
  const location = useLocation();
  const sheet = useSelector((store) => store.currentWorklog.worklogSheet.sheet);
  // const usersList = useSelector((store) => store.people.users);
  const [clUpdating, setClUpdating] = useState(false);

  const displaySheetState = useMemo(() => historyState?.sheetState || sheet, [sheet, historyState])

  const canEditCrewLeader = useCanEditWorklogCrewLeader(sheet);
  // hasAccess already carries the history guard; the helper adds the own-row CL restriction.
  const clEditable = hasAccess && canEditCrewLeader(workerData);

  const lcState = useSelector(selectLaborClassificationsState);
  const laborClassificationTooltip = useMemo(() => {
    const names = getWorkerLcOverrideNames(workerData, displaySheetState, lcState?.byId);
    if (!names.length) return null;
    return `Labor Classifications: ${names.join(', ')}`;
  }, [workerData, displaySheetState, lcState]);

  const setForeman = async () => {
    setClUpdating(true);
    const sheetId = sheet._id;
    const userId = workerData._id;
    const value = !workerData.foreman;

    dispatch(postWorklogCL({ foreman: value }, sheetId, userId));

    if (sheet.submittedAt) {
      dispatch(currentWorklogActions.submittedChangesModalWlChanged());
    }
    setClUpdating(false);
  };

  const isWorkerScheduleMobile = isMobileSchedule() && /schedule-user/i.test(location.pathname);

  const workerDiffs = historyState?.diff?.workers?.[historyItem?.prevIndex];
  const hasClChanged = !!historyState && (
    (historyItem?.prevState && workerDiffs?.foreman?.length)
  );
  const isItemChanged = !!historyState && historyItem?.action === 'changed';
  const isItemAdded = !!historyState && historyItem?.action === 'added';

  return (
    <Grid
      container
      className={classnames(
        classes.crewRow,
        worklogClasses.resourceBlockItem,
        isItemAdded ? worklogClasses.showDiff : null,
        removed ? worklogClasses.showDiffRemoved : null,
      )}
    >
      <Grid item xs={12} md={3}>
        <div className={classes.flexBox} style={{ height: 25 }}>
          <div
            className={classnames(classes.inlineFlex, hasAccess && classes.clickable)}
            onClick={hasAccess ? openUserWorklogs : () => null}
          >
            <div
              className={classnames(
                classes.photoWrapper,
                classes['dayBorder'],
                classes[`${workerData && workerData.shifts.timeOfDay.toLowerCase()}Border`]
              )}
            >
              <img src={photoPlaceholder} className={classes.photo} />
            </div>
            <Typography
              variant="body1"
              color="textPrimary"
              className={classnames(
                classes.name,
                classes['day'],
                classes[workerData && workerData.shifts.timeOfDay.toLowerCase()]
              )}
            >
              {workerData && workerData.username}
            </Typography>
          </div>

          <div
            className={classnames(
              classes.actionsCluster,
              !laborClassificationTooltip && reserveLcIconSpace && classes.actionsClusterNoIcon,
            )}
          >
            <span
              className={classnames(
                (isItemChanged && hasClChanged)
                  ? workerData.foreman ? worklogClasses.showDiff : worklogClasses.showDiffRemoved
                  : null
              )}
            >
              <Button
                variant="text"
                className={workerData.foreman ? classes.activeButton : classes.inactiveButton}
                onClick={clEditable ? setForeman : null}
                disableRipple
                disabled={clUpdating || !clEditable}
              >
                (CL)
              </Button>
            </span>
            {laborClassificationTooltip && (
              <InfoTooltip
                content={laborClassificationTooltip}
                iconSize="small"
                placement="left"
              />
            )}
          </div>
        </div>
      </Grid>
      <Grid container item xs={8} md={8} className={classes.resourceHoursValues}>
        {(workerData && (
          workerData.hours?.length
          || workerData.startTime
          || workerData.startTimeDate
          || historyItem?.prevState?.hours?.length
        )) ? (
          <TimeInfo
            workerData={workerData}
            classes={classes}
            sheet={displaySheetState}
            getFormattedHoursRange={getFormattedHoursRange}
            isWorkerScheduleMobile={isWorkerScheduleMobile}
            isItemAdded={isItemAdded}
            isItemChanged={isItemChanged}
            historyHours={historyItem?.prevState?.hours}
            historyState={historyState}
          />
        ) : null}
      </Grid>
      <Grid item container xs={1} justifyContent={'flex-end'} spacing={0}></Grid>
    </Grid>
  );
};

CrewUser.propTypes = {
  classes: propTypes.object.isRequired,
  workerData: propTypes.object.isRequired,
  userData: propTypes.object.isRequired,
  getFormattedHoursRange: propTypes.func.isRequired,
  openUserWorklogs: propTypes.func.isRequired,
  hasAccess: propTypes.bool.isRequired,
  reserveLcIconSpace: propTypes.bool,
};

CrewUser.defaultProps = {
  reserveLcIconSpace: false,
};

export default withStyles(styles)(CrewUser);
